import { ApiResponse } from '../types'
import { AxiosClient } from '../utils/clients'

export enum StreamStyle {
    Basic = 'basic',
    Image = 'images',
    Video = 'video',
}

export enum StreamMessageType {
    NOTICE = 'notice',
    INVOICE = 'invoice',
    PAYMENT_NOTIFICATION = 'payment-notification',
    FRAUD_BILL_NOTIFICATION = 'fraud-bill-notification',
    NOTIFICATION = 'notification',
    ISSUE_NOTIFICATION = 'issue-notification',
    BILLING_TYPE_CHANGE = 'billing-type-change',
    CONNECTION_FEE_GENERATED = 'connection-fee-generated',
    CONNECTION_FEE_PAID = 'connection-fee-paid',
    APPLICATION_FEE_GENERATED = 'application-fee-generated',
    APPLICATION_FEE_PAID = 'application-fee-paid',
    INSPECTION_INITIATED = 'inspection-initiated',
    INSPECTION_SUCCESSFUL = 'inspection-successful',
    CONTRACT_GENERATED = 'contract-generated',
    INSTALLATION_INITIATED = 'installation-initiated',
    INSTALLATION_FAILED = 'installation-failed',
    INSTALLATION_SUCCESSFUL = 'installation-successful',
    CONTRACT_SIGNED = 'contract-signed',
    SERVICE_REQUEST_COMPLETED = 'service-request-completed',
    TARIFF_CLASS_UPDATED = 'tariff-class-updated',
    NEW_METER_INSTALLED = 'new-meter-installed',
    METER_REPLACED = 'meter-replaced',
    REQUEST_ACCOUNT_DETAILS_CHANGE_REJECTED = 'request-account-details-change-rejected',
    REQUEST_ACCOUNT_DETAILS_CHANGE_APPROVED = 'request-account-details-change-approved',
    ACCOUNT_DETAILS_UPDATED = 'account-details-Updated',
    NEW_METER_READING = 'new-meter-reading',
    COMPLAINT = 'complaint',
    PAY_SMALL_SMALL = 'pay-small-small',
    PAY_SMALL_SMALL_DEBIT_FAILURE = 'pay-small-small-debit-failure',
    METER_TRANSFER_REQUEST = 'meter-transfer-request',
    TEMPORARY_DISCONNECTION_REQUEST = 'temporary-disconnection-request',
    RECONNECTION_REQUEST = 'reconnection-request',
    DELAYED_INSTALLATION = 'delayed-installation',
    NEW_SERVICE_REQUEST = 'new-service-request',
    STATEMENT_REQUEST = 'statement-request',
    STOLEN_METER = 'stolen meter',
    FAULTY_METER = 'faulty meter',
    FAULTY_METER_2 = 'Faulty Meter',
    REPORT_ISSUE_WITH_POSTPAID_BILL = 'issue with postpaid bill',
    ILLEGAL_CONNECTION = 'illegal connections',
    ILLEGAL_CONNECTION_2 = 'illegal connection',
    CHANGE_ACCOUNT_DETAILS = 'change-account-details',
    METER_READING_EXCEPTION = 'meter-reading-exception',
    METER_READING_ANOMALY = 'meter-reading-anomaly',
    METER_NOT_FOUND = 'prepaid-meter-not-found',
    METER_NOT_FOUND_2 = 'meter not found',
    METER_NOT_FOUND_3 = 'prepaid meter not found',
    TASK_PROGRESS = 'task_progress',
    REQUEST_PROGRESS = 'request_progress',
    TASK_ASSIGNMENT_NOTICE = 'task-assignment-notice',
    POSTPAID_BILL = 'postpaid-cutoff-notification',
    STATEMENT_REQUEST_FAILED = 'statement-request-failed',
    METER_CARD_REPLACEMENT = 'meter card replacement',

    // Meter Replacement
    METER_GOT_REPLACED_SUBMITTED = 'meter-got-replaced-submitted',
    METER_REPLACEMENT_TASK_ASSIGNED = 'meter-replacement-task-assigned',
    METER_REPLACED_EVENT = 'meter-replaced-task',
    METER_REPLACEMENT_REMAINING_BALANCE = 'meter-replacement-remaining-balance',
    METER_REPLACEMENT_CREDIT_BALANCE = 'meter-replacement-credit-balance',
    METER_REPLACEMENT_DEBT_BALANCE = 'meter-replacement-debt-balance',
    METER_REPLACEMENT_REVIEWED_CREDIT_BALANCE = 'meter-replacement-reviewed-credit-balance', // when credit is available
    METER_REPLACEMENT_CREDIT_TRANSFER_BALANCE = 'meter-replacement-credit-transfer-balance', // when receipt is available
    METER_REPLACEMENT_OUTSTANDING_BALANCE_PAID = 'meter-replacement-outstanding-balance-paid',
    METER_BALANCE_REPORTED = 'meter-balance-reported',
    METER_REPLACED_BALANCE = 'meter-replaced-balance',
    METER_REPLACEMENT = 'meter-replacement',

    // vendor debt payment
    VENDOR_DEBT_PAYMENT_INVOICE = 'vendor-debt-payment',
    VENDOR_DEBT_GHANA_GOV_INVOICE = 'vendor-debt-payment-notification',
}

export type NotificationStream = {
    id: string
    messageType: StreamMessageType
    audience: null | string
    style: StreamStyle
    data: null | string
    isBulk: boolean
    size: number
    agencyName: string
    messageTitle: string
    description: string
    deleted: boolean
    createdAt: string
    updatedAt: null | string
    image: string
    agencyLogo: string | null
    video: string
    images: string[]
    createdBy: null | string
    updatedBy: any
    status: string | null
    requestCode: string
    amount: number
    invoiceId: any
    trackingCode: string
    customerStatementPreviewImageUrl: string
    meterCreditTransferId: string | null
    meterId: string | null
    meterNumber: string | null
    accountNumber: string | null
}

export type StreamFilter = {
    Page: number
    PageSize: number
    SortColumn: string
    SortDir: string
}

export type StreamResponse = ApiResponse<{
    results: NotificationStream[]
    lowerBound: number
    upperBound: number
    pageIndex: number
    totalPages: number
    pageSize: number
    totalCount: number
}>

export interface GetServiceRequestEventDetails {
    serviceRequestInformation?: ServiceRequestInformation
    inspectionInformation?: InspectionInformation
    contractInformation?: Information
    installationInformation?: Information
    invoiceDetails?: InvoiceDetails
}
export interface Information {
    status: string
}
export interface InspectionInformation {
    estimatorName: null
    estimatorNumber: null
    status: string
}
export interface InvoiceDetails {
    invoiceId: string
    invoiceNumber: string
    customerName: string
    customerPhoneNumber: string
    createdAt: string
    applicationFee: number
    applicationFeeStatus: string
    connectionFeeStatus: string
    connectionFee: number
}
export interface ServiceRequestInformation {
    serviceRequestCode: string
    date: string
    category: string
    type: string
}

export interface GetEventDetails {
    serviceRequestCode: string
    accountNumber: string
    customerNumber: string
    servicePointNumber: string
    propertyNumber: string | null
    tariffClassCode: string | null
    tariffClassName: string | null
    oldMeterNumber: string | null
    newMeterNumber: string | null
    oldMeterCategory: string | null
    newMeterCategory: string | null
    oldMeterType: null | {
        modelCode: string
        modelName: string
        brandName: string
        brandCode: string
        systemCode: string
        systemName: string
        phase: string
        voltage: string
        currentRating: string
    }
    newMeterType: null | {
        modelCode: string
        modelName: string
        brandName: string
        brandCode: string
        systemCode: string
        systemName: string
        phase: string
        voltage: string
        currentRating: string
    }
    customerName: string | null
    customerDateOfBirth: string | null
    ghanaCardNumber: string | null
    ghanaCardIssueDate: string | null
    ghanaCardExpiryDate: string | null
    dailyConsumptionTrend:
        | {
              fromDate: string
              toDate: string
              dailyConsumption: number
          }[]
        | null
    readingLocationName: string
    currentDailyConsumption: number
    exceptionReason: string | null
    anomalyReason: string
    meterCategory: string | null
    meterNumber: string | null
    meterType: null | {
        modelCode: string
        modelName: string
        brandName: string
        brandCode: string
        systemCode: string
        systemName: string
        phase: string
        voltage: string
        currentRating: string
    }
}

export default class DashboardRepository {
    private BASE_URL = 'https://api-ecg-streams.gov-gh.com'
    // private BASE_URL = 'https://fancy-hounds-leave.loca.lt'
    private REPORTS_BASE_URL = 'https://api-ecg-reports.gov-gh.com'

    constructor(private client: AxiosClient) {}

    async getStreams(filters: Partial<StreamFilter>) {
        const res = await this.client.get<StreamResponse>(`${this.BASE_URL}/Notices`, {
            params: filters,
        })

        return res.data
    }

    async getServiceRequestEventDetails({ accountNumber, eventName }: { accountNumber: string; eventName: string }) {
        const res = await this.client.get<ApiResponse<GetEventDetails>>(
            `${this.REPORTS_BASE_URL}/customerreports/${accountNumber}/${eventName}/event-details`,
        )
        return res.data
    }

    async downloadFileFromUrl(url: string) {
        const res = await this.client.get(`${url}`, {
            responseType: 'blob',
        })
        return res.data
    }
}
